import { match } from "ts-pattern";

import { emptySelection, allSelection, subsetSelection } from "./list-box";
import type { Item, ListBoxSelection } from "./list-box";

/**
 * The typed state behind `Select`: plain `kind`-tagged data plus pure
 * `(state, ...) => state` helpers. `Select` is a view over a `SelectState` and
 * reports gestures as `SelectMsg`s; the consumer owns selection and search
 * text, and does all filtering.
 */

export type { Item }; // the app has no other way to name it
export type NonEmptyArray<T> = readonly [T, ...T[]];
/** Alias so the app never sees ListBox's name (ListBox is pallet-internal). */
export type MultipleSelection<T extends Item = Item> = ListBoxSelection<T>;
// Reach the app as `selectState.subsetSelection(...)` etc.
export { emptySelection, allSelection, subsetSelection };

/** The field's accessible name: a visible label or aria-only. `orientation` only exists where a <label> is drawn. */
export type Label =
  | { kind: "visible"; text: string; orientation: "row" | "column" }
  | { kind: "aria"; text: string };

export type SingleSelection<T extends Item = Item> =
  { kind: "empty" } | { kind: "one"; item: T };
export type AlwaysSelection<T extends Item = Item> = {
  kind: "one";
  item: T;
};

export type Options<T extends Item = Item> =
  | { kind: "loading"; label: string }
  // May be `[]`; searchable lists use this.
  | { kind: "ready"; items: ReadonlyArray<T>; emptyLabel: string }
  // Static, unsearched.
  | { kind: "fixed"; items: NonEmptyArray<T> };

/** Consumer-owned: `text` is the box's value (`""` = nothing typed). Pallet never filters. */
export type Search =
  { kind: "off" } | { kind: "enabled"; placeholder: string; text: string };

interface Live<T extends Item, K extends string, S> {
  kind: K;
  label: Label;
  options: Options<T>;
  search: Search;
  selection: S;
}
export type DisabledState = { kind: "disabled"; label: Label; reason: string };
// x + required placeholder
export type SingleState<T extends Item = Item> = Live<
  T,
  "single",
  SingleSelection<T>
> & { placeholder: string };
// no x, no placeholder
export type AlwaysState<T extends Item = Item> = Live<
  T,
  "always",
  AlwaysSelection<T>
>;
export type MultipleState<T extends Item = Item> = Live<
  T,
  "multiple",
  MultipleSelection<T>
> & { placeholder: string };
export type LiveState<T extends Item = Item> =
  SingleState<T> | AlwaysState<T> | MultipleState<T>;
export type SelectState<T extends Item = Item> = DisabledState | LiveState<T>;

/** What `Select` tells the consumer. Callers `match(msg)` and no-op arms that cannot arrive for their state. */
export type SelectMsg<T extends Item = Item> =
  // typing, and the clear-search button (text: "")
  | { kind: "searched"; text: string }
  // single / always
  | { kind: "picked"; item: T }
  // multiple
  | { kind: "pickedN"; selection: MultipleSelection<T> }
  // the `x`; single / multiple only
  | { kind: "cleared" }
  // popover opened
  | { kind: "opened" }
  // popover closed, any cause; pallet does NOT reset search
  | { kind: "closed" };

// Constructors: only what has no sensible default, as a named struct.
// Search starts `off`; selection starts empty.
export function single<T extends Item>(a: {
  label: Label;
  placeholder: string;
  options: Options<T>;
}): SingleState<T> {
  return {
    kind: "single",
    label: a.label,
    placeholder: a.placeholder,
    options: a.options,
    search: { kind: "off" },
    selection: { kind: "empty" },
  };
}

export function multiple<T extends Item>(a: {
  label: Label;
  placeholder: string;
  options: Options<T>;
}): MultipleState<T> {
  return {
    kind: "multiple",
    label: a.label,
    placeholder: a.placeholder,
    options: a.options,
    search: { kind: "off" },
    selection: emptySelection,
  };
}

export function always<T extends Item>(a: {
  label: Label;
  options: Options<T>;
  selected: T;
}): AlwaysState<T> {
  return {
    kind: "always",
    label: a.label,
    options: a.options,
    search: { kind: "off" },
    selection: { kind: "one", item: a.selected },
  };
}

export function disabled(a: { label: Label; reason: string }): DisabledState {
  return { kind: "disabled", label: a.label, reason: a.reason };
}

// Modifiers: state first, SAME arm back (`S` in, `S` out), so
// `withSearch(single(..), ..)` is still a SingleState.

/**
 * Turns the search box on with the consumer's current `text`. `S extends
 * LiveState -> S` hands back the arm it was given, so `withSearch(single(..), ..)`
 * is still a `SingleState` (and `withPick` still accepts it). A `DisabledState`
 * is a compile error: it has no box to type in.
 */
export function withSearch<S extends LiveState>(
  s: S,
  search: { placeholder: string; text: string },
): S {
  return { ...s, search: { kind: "enabled", ...search } };
}

/**
 * Empties the search box (`text: ""`); returns `s` unchanged when search is
 * `off`. Same arm back (`S` in, `S` out), so a `single` stays a `single`. For
 * code that holds a whole `SelectState`; most callers store only the text and
 * `setText("")` on `closed`.
 */
export function clearSearch<S extends LiveState>(s: S): S {
  if (s.search.kind === "off") return s;
  return { ...s, search: { ...s.search, text: "" } };
}

/**
 * Sets the one selected row. Only `single | always` take a pick (`multiple`
 * has `withPicks`; `disabled` carries no selection), so the wrong arm is a
 * compile error, not a runtime no-op. `S` in, `S` out: `always` stays `always`.
 */
export function withPick<
  T extends Item,
  S extends SingleState<T> | AlwaysState<T>,
>(s: S, item: T): S {
  return { ...s, selection: { kind: "one", item } };
}

/**
 * Sets the whole selection of a `multiple`. Same arm back (`S` in, `S` out).
 *
 * `T` is read off `S` with `infer` on purpose. The obvious
 * `<T extends Item, S extends MultipleState<T>>(s: S, selection: MultipleSelection<T>)`
 * infers `T` from `selection` alone, and `emptySelection` / `allSelection` are
 * `ListBoxSelection<never>`, so `T` becomes `never` and every real state is
 * rejected. Deriving `T` from `S` makes the selection's item type follow the
 * state's, and `never` for a non-`multiple` `S` makes the wrong arm a compile error.
 */
export function withPicks<S extends MultipleState>(
  s: S,
  selection: S extends MultipleState<infer T> ? MultipleSelection<T> : never,
): S {
  return { ...s, selection };
}

// Helpers

export function ariaLabel(text: string): Label {
  return { kind: "aria", text };
}

export function visibleLabel(a: {
  text: string;
  orientation: "row" | "column";
}): Label {
  return { kind: "visible", text: a.text, orientation: a.orientation };
}

export function loading(label: string): Options<never> {
  return { kind: "loading", label };
}

export function ready<T extends Item>(a: {
  items: ReadonlyArray<T>;
  emptyLabel: string;
}): Options<T> {
  return { kind: "ready", items: a.items, emptyLabel: a.emptyLabel };
}

export function fixed<T extends Item>(items: NonEmptyArray<T>): Options<T> {
  return { kind: "fixed", items };
}

/** Predicate for a consumer-side `.filter`: case-insensitive label includes. `""` matches all. */
export function byLabel<T extends Item>(text: string): (item: T) => boolean {
  const needle = text.toLowerCase();
  return (item) => item.label.toLowerCase().includes(needle);
}

function itemsOf<T extends Item>(s: LiveState<T>): ReadonlyArray<T> {
  return match(s.options)
    .with({ kind: "loading" }, () => [])
    .with({ kind: "ready" }, { kind: "fixed" }, (o) => o.items)
    .exhaustive();
}

/** What the field shows/counts. `multiple`'s `all` resolves against `options` (`ready`/`fixed` items; `[]` while loading). */
export function selectedItems<T extends Item>(
  s: SelectState<T>,
): ReadonlyArray<T> {
  return match(s)
    .with({ kind: "disabled" }, () => [])
    .with({ kind: "single", selection: { kind: "one" } }, (m) => [
      m.selection.item,
    ])
    .with({ kind: "single" }, () => [])
    .with({ kind: "always" }, (m) => [m.selection.item])
    .with({ kind: "multiple", selection: { kind: "empty" } }, () => [])
    .with({ kind: "multiple", selection: { kind: "all" } }, (m) => itemsOf(m))
    .with(
      { kind: "multiple", selection: { kind: "subset" } },
      (m) => m.selection.items,
    )
    .exhaustive();
}
